import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { logWarn } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Settings, Lock, Trash2, Store, Palette, Award, ShoppingBag, MessageCircle,
  ShieldCheck, Info, KeyRound, EyeOff, Eye,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMAS = [
  { id: "grafite", nome: "Grafite", cor: "#0f172a" },
  { id: "pedra", nome: "Pedra", cor: "#292524" },
  { id: "carvao", nome: "Carvão", cor: "#18181b" },
  { id: "marinho", nome: "Marinho", cor: "#1e293b" },
];

const ABA_LABELS = {
  "lojas": "Lojas", "tema": "Tema", "premiacao": "Premiação",
  "catalogo": "Catálogo", "seguranca": "Segurança",
};

const SISTEMA_ABA_LABELS = {
  receitas: "Receitas", "nova-receita": "Nova Receita", "alertas-caixa": "Alertas de Caixa",
  metas: "Metas", lentes: "Lentes", mensagens: "Mensagens (Kanban)", clientes: "Clientes",
  dashboard: "Dashboard", estoque: "Estoque", catalogo: "Catálogo (Admin)", crm: "CRM",
  configuracoes: "Configurações",
};

export default function Configuracoes() {
  const [aba, setAba] = useState("lojas");

  // Gate opcional: só pede senha se o tenant tiver configurado senha de 'configuracoes'
  const [precisaSenha, setPrecisaSenha] = useState(null); // null = checando, true/false = decidido
  const [autorizado, setAutorizado] = useState(false);
  const [senha, setSenha] = useState("");

  // Config principal
  const [cfg, setCfg] = useState(null);
  const [lojas, setLojas] = useState([]);
  const [lojasIds, setLojasIds] = useState([]); // uids estáveis por linha (não vão para o backend)
  const [slugsPublicos, setSlugsPublicos] = useState({});
  const [tema, setTema] = useState("grafite");
  const [faixas, setFaixas] = useState([]);
  const [faixasIds, setFaixasIds] = useState([]); // uids estáveis por linha
  const [catalogo, setCatalogo] = useState({ lojas: {}, mensagem_padrao: "" });
  const uidRef = useRef(0);
  const genUid = () => { uidRef.current += 1; return `row-${uidRef.current}`; };

  // Segurança
  const [seg, setSeg] = useState(null);
  const [senhasForm, setSenhasForm] = useState({ estoque: "", dinheiro: "", configuracoes: "" });
  const [abasBloqueadas, setAbasBloqueadas] = useState([]);
  const [atualSeg, setAtualSeg] = useState("");
  const [showPwd, setShowPwd] = useState({});

  // --- Gate ---
  useEffect(() => {
    axios.get(`${API}/config/seguranca`).then((r) => {
      const temSenhaCfg = !!r.data.senhas_configuradas?.configuracoes;
      setPrecisaSenha(temSenhaCfg);
      if (!temSenhaCfg) setAutorizado(true);
    }).catch(() => { setPrecisaSenha(false); setAutorizado(true); });
  }, []);

  const carregar = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/config`);
      setCfg(r.data);
      const ls = r.data.lojas || [];
      setLojas(ls);
      setLojasIds(ls.map(() => genUid()));
      setTema(r.data.tema || "grafite");
      const fx = r.data.premiacao_faixas || [];
      setFaixas(fx);
      setFaixasIds(fx.map(() => genUid()));
      setCatalogo(r.data.catalogo || { lojas: {}, mensagem_padrao: "" });
    } catch { toast.error("Erro ao carregar configurações"); }
    try {
      const r2 = await axios.get(`${API}/config/lojas`);
      setSlugsPublicos(r2.data?.slugs || {});
    } catch (e) { logWarn("config: slugs públicos indisponíveis", e); }
    try {
      const r3 = await axios.get(`${API}/config/seguranca`);
      setSeg(r3.data);
      setAbasBloqueadas(r3.data.abas_bloqueadas || []);
    } catch (e) { logWarn("config: segurança indisponível", e); }
  }, []);

  useEffect(() => { if (autorizado) carregar(); }, [autorizado, carregar]);

  const entrar = async () => {
    try {
      await axios.post(`${API}/config/verificar-senha`, { senha });
      setAutorizado(true);
    } catch { toast.error("Senha incorreta"); }
  };

  const salvar = async (campos, msg) => {
    try {
      const body = { ...campos };
      if (precisaSenha) body.senha = senha;
      const r = await axios.put(`${API}/config`, body);
      setCfg(r.data);
      toast.success(msg || "Salvo");
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  };

  const salvarLojas = () => salvar({ lojas }, "Lojas atualizadas");
  const salvarTema = async () => {
    await salvar({ tema }, "Tema aplicado");
    localStorage.setItem("tema", tema);
    window.dispatchEvent(new Event("tema-updated"));
  };
  const salvarFaixas = () => salvar({
    premiacao_faixas: faixas.map((f) => ({ min: Number(f.min) || 0, premio: Number(f.premio) || 0 })),
  }, "Tabela de premiação salva");
  const updCatLoja = (loja, campo, valor) => setCatalogo((c) => ({
    ...c, lojas: { ...(c.lojas || {}), [loja]: { ...((c.lojas || {})[loja] || {}), [campo]: valor } },
  }));
  const salvarCatalogo = () => salvar({ catalogo }, "Catálogo salvo");
  const slugLoja = (l) => (l || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();

  const salvarSeguranca = async () => {
    const senhasPayload = {};
    for (const [k, v] of Object.entries(senhasForm)) {
      if (v !== "" && v !== null && v !== undefined) senhasPayload[k] = v;
    }
    try {
      const body = { atual: atualSeg || senha, senhas: senhasPayload, abas_bloqueadas: abasBloqueadas };
      const r = await axios.put(`${API}/config/seguranca`, body);
      setSeg(r.data);
      setSenhasForm({ estoque: "", dinheiro: "", configuracoes: "" });
      setAtualSeg("");
      toast.success("Segurança atualizada");
      // Se a senha de configurações foi definida ou mudou, mantém a sessão autorizada
      if (r.data.senhas_configuradas?.configuracoes && !precisaSenha) setPrecisaSenha(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar segurança");
    }
  };

  const toggleBloqueio = (nome) => {
    setAbasBloqueadas((cur) => (cur.includes(nome) ? cur.filter((x) => x !== nome) : [...cur, nome]));
  };

  // Gate rendering
  if (precisaSenha === null) {
    return <div className="p-10 text-center text-slate-400" data-testid="config-loading">Carregando…</div>;
  }
  if (precisaSenha && !autorizado) {
    return (
      <div className="max-w-md mx-auto mt-10" data-testid="config-gate">
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-slate-600" />
              <CardTitle>Configurações — acesso restrito</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <Label>Senha de Configurações</Label>
            <Input type="password" value={senha} onChange={(e) => setSenha(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && entrar()} placeholder="Senha de acesso" data-testid="config-senha" />
            <Button onClick={entrar} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="config-entrar">Entrar</Button>
            <p className="text-xs text-slate-400 pt-2">Esta senha foi definida na aba Segurança.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="config-page">
      <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
        <Settings className="w-7 h-7" /> Configurações
      </h1>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {Object.entries(ABA_LABELS).map(([k, lbl]) => {
          const Icon = { lojas: Store, tema: Palette, premiacao: Award, catalogo: ShoppingBag, seguranca: ShieldCheck }[k];
          return (
            <button key={k} onClick={() => setAba(k)} data-testid={`config-tab-${k}`}
              className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors ${
                aba === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
              }`}>
              <Icon className="w-4 h-4" /> {lbl}
            </button>
          );
        })}
      </div>

      {/* LOJAS */}
      {aba === "lojas" && (
        <Card className="bg-white border-slate-200" data-testid="config-lojas">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Store className="w-4 h-4 text-slate-500" />
              <CardTitle className="text-base">Lojas</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {lojas.map((l, i) => (
              <div key={lojasIds[i] || `loja-${i}`} className="flex items-center gap-2" data-testid={`config-loja-${i}`}>
                <Input value={l} onChange={(e) => setLojas(lojas.map((x, j) => j === i ? e.target.value : x))} />
                <Button size="icon" variant="ghost" onClick={() => { setLojas(lojas.filter((_, j) => j !== i)); setLojasIds(lojasIds.filter((_, j) => j !== i)); }}
                  data-testid={`config-loja-del-${i}`} disabled={lojas.length <= 1} title={lojas.length <= 1 ? "Mantenha ao menos uma loja" : "Remover"}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 mt-3" data-testid="config-info-novas-lojas">
              <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">
                Para <strong>adicionar novas lojas</strong>, solicite ao suporte administrativo. Aqui você pode renomear ou remover lojas existentes.
              </p>
            </div>
            <Button onClick={salvarLojas} className="bg-slate-900 hover:bg-slate-800 text-white mt-2" data-testid="config-salvar-lojas">
              Salvar lojas
            </Button>
          </CardContent>
        </Card>
      )}

      {/* TEMA */}
      {aba === "tema" && (
        <Card className="bg-white border-slate-200" data-testid="config-tema">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-slate-500" />
              <CardTitle className="text-base">Tema de cores (menu lateral)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {TEMAS.map((t) => (
                <button key={t.id} onClick={() => setTema(t.id)} data-testid={`config-tema-${t.id}`}
                  className={`rounded-lg border-2 p-3 text-left transition-all ${
                    tema === t.id ? "border-slate-900 ring-2 ring-slate-200" : "border-slate-200"
                  }`}>
                  <div className="w-full h-10 rounded mb-2" style={{ backgroundColor: t.cor }} />
                  <span className="text-sm font-medium">{t.nome}</span>
                </button>
              ))}
            </div>
            <Button onClick={salvarTema} className="bg-slate-900 hover:bg-slate-800 text-white mt-3" data-testid="config-salvar-tema">
              Aplicar tema
            </Button>
          </CardContent>
        </Card>
      )}

      {/* PREMIAÇÃO */}
      {aba === "premiacao" && (
        <Card className="bg-white border-slate-200" data-testid="config-premiacao">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-slate-500" />
              <CardTitle className="text-base">Tabela de Premiação (valor mínimo da lente → prêmio)</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {faixas.map((f, i) => (
              <div key={faixasIds[i] || `faixa-${i}`} className="flex items-center gap-2" data-testid={`config-faixa-${i}`}>
                <span className="text-xs text-slate-400 w-16">a partir de</span>
                <Input type="number" value={f.min} onChange={(e) => setFaixas(faixas.map((x, j) => j === i ? { ...x, min: e.target.value } : x))} className="w-28" />
                <span className="text-xs text-slate-400">→ R$</span>
                <Input type="number" value={f.premio} onChange={(e) => setFaixas(faixas.map((x, j) => j === i ? { ...x, premio: e.target.value } : x))} className="w-24" />
                <Button size="icon" variant="ghost" onClick={() => { setFaixas(faixas.filter((_, j) => j !== i)); setFaixasIds(faixasIds.filter((_, j) => j !== i)); }}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button variant="outline" onClick={() => { setFaixas([...faixas, { min: 0, premio: 0 }]); setFaixasIds([...faixasIds, genUid()]); }} className="mt-1" data-testid="config-add-faixa">
              Nova faixa
            </Button>
            <div>
              <Button onClick={salvarFaixas} className="bg-slate-900 hover:bg-slate-800 text-white mt-2" data-testid="config-salvar-faixas">
                Salvar premiação
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CATÁLOGO */}
      {aba === "catalogo" && (
        <Card className="bg-white border-slate-200" data-testid="config-catalogo">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-slate-500" />
              <CardTitle className="text-base">Catálogo — WhatsApp e informações por loja</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500">Mensagem padrão do botão "Comprar no WhatsApp"</Label>
              <Input value={catalogo.mensagem_padrao || ""} onChange={(e) => setCatalogo({ ...catalogo, mensagem_padrao: e.target.value })}
                placeholder="Olá! Tenho interesse neste modelo:" data-testid="config-cat-mensagem" />
            </div>
            {lojas.map((l) => {
              const c = (catalogo.lojas || {})[l] || {};
              const slug = slugLoja(l);
              const slugPublico = slugsPublicos[l] || slug;
              return (
                <div key={l} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`config-cat-loja-${slug}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      {l}
                      {c.oculto ? <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">oculta</span> : null}
                    </p>
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid={`config-cat-oculto-${slug}`}>
                      <span>Ocultar do catálogo público</span>
                      <Switch
                        checked={!!c.oculto}
                        onCheckedChange={(v) => updCatLoja(l, "oculto", v)}
                        className="data-[state=checked]:bg-red-500 data-[state=unchecked]:bg-slate-300"
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-slate-500">WhatsApp (com DDD, ex: 5547988887777)</Label>
                      <Input value={c.whatsapp || ""} onChange={(e) => updCatLoja(l, "whatsapp", e.target.value)}
                        placeholder="55DDDNUMERO" data-testid={`config-cat-whatsapp-${slug}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Horário de atendimento</Label>
                      <Input value={c.horario || ""} onChange={(e) => updCatLoja(l, "horario", e.target.value)} placeholder="Seg-Sex 9h-18h30" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-slate-500">Endereço</Label>
                      <Input value={c.endereco || ""} onChange={(e) => updCatLoja(l, "endereco", e.target.value)} placeholder="Rua, nº - bairro, cidade" />
                    </div>
                  </div>
                  {c.oculto ? (
                    <p className="text-xs text-slate-400 italic">Link público retorna 404 enquanto esta loja estiver oculta.</p>
                  ) : (
                    <a href={`/catalogo/${slugPublico}`} target="_blank" rel="noreferrer"
                      className="text-xs text-emerald-600 hover:underline inline-flex items-center gap-1" data-testid={`config-cat-link-${slug}`}>
                      <MessageCircle className="w-3 h-3" />Abrir link público: /catalogo/{slugPublico}
                    </a>
                  )}
                </div>
              );
            })}
            <Button onClick={salvarCatalogo} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="config-salvar-catalogo">
              Salvar catálogo
            </Button>
          </CardContent>
        </Card>
      )}

      {/* SEGURANÇA */}
      {aba === "seguranca" && seg && (
        <div className="space-y-6" data-testid="config-seguranca">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-slate-500" />
                <CardTitle className="text-base">Senhas internas do sistema</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500">
                Defina uma senha específica para cada operação sensível. Deixe em branco para manter a senha atual.
                Para <strong>remover</strong> uma senha, apague o campo correspondente e salve com a opção "limpar" (digite <code>remover</code>).
              </p>

              {Object.entries(seg.funcoes_disponiveis).map(([k, lbl]) => (
                <div key={k} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`seguranca-senha-${k}`}>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold text-slate-800">{lbl}</Label>
                    <span className={`text-xs ${seg.senhas_configuradas?.[k] ? "text-emerald-600" : "text-slate-400"}`}>
                      {seg.senhas_configuradas?.[k] ? "Senha definida" : "Sem senha (acesso livre)"}
                    </span>
                  </div>
                  <div className="relative">
                    <Input
                      type={showPwd[k] ? "text" : "password"}
                      value={senhasForm[k] || ""}
                      onChange={(e) => setSenhasForm((f) => ({ ...f, [k]: e.target.value }))}
                      placeholder={seg.senhas_configuradas?.[k] ? "Nova senha (deixe vazio para manter)" : "Definir senha"}
                      data-testid={`seguranca-input-${k}`}
                      className="pr-10"
                    />
                    <button type="button" onClick={() => setShowPwd((s) => ({ ...s, [k]: !s[k] }))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPwd[k] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}

              {seg.senhas_configuradas?.configuracoes && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <Label className="text-xs text-amber-800 font-semibold">Senha atual de Configurações (obrigatória para alterar segurança)</Label>
                  <Input type="password" value={atualSeg} onChange={(e) => setAtualSeg(e.target.value)}
                    placeholder="Senha atual" data-testid="seguranca-atual" className="mt-1" />
                </div>
              )}

              <Button onClick={salvarSeguranca} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="seguranca-salvar">
                Salvar senhas
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-slate-500" />
                <CardTitle className="text-base">Bloquear abas do sistema</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-slate-500">
                As abas marcadas pedirão a senha de <strong>Configurações</strong> ao serem abertas.
                Se a senha de Configurações não estiver definida, o bloqueio fica inativo.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {seg.abas_disponiveis.map((nome) => (
                  <label key={nome} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid={`seguranca-aba-${nome}`}>
                    <Checkbox checked={abasBloqueadas.includes(nome)} onCheckedChange={() => toggleBloqueio(nome)} />
                    <span className="text-sm text-slate-700">{SISTEMA_ABA_LABELS[nome] || nome}</span>
                  </label>
                ))}
              </div>
              <Button onClick={salvarSeguranca} className="bg-slate-900 hover:bg-slate-800 text-white mt-2" data-testid="seguranca-salvar-abas">
                Salvar bloqueios
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
